import { SKIP_GLOBAL_ERROR_HANDLING_FLAG } from '@/composables/ErrorHandler';
import { RouterHelpers } from '@/router/routeHelpers';
import { istBenutzerAngemeldet } from '@/utils/auth';
import type { App } from 'vue';
import type { RouteLocationRaw, Router } from 'vue-router';

/**
 * Ermittelt die Fehlerroute inkl. Fehlerdetails im Route-State.
 */
export const getFehlerRoute = (router: Router, error: any, additionalInfo?: string): RouteLocationRaw => {
    const response = error?.response;
    const status = response?.status ?? error?.status;

    const data = response?.data;

    // Primär: Details aus Response.data (ProblemDetails nach RFC 7807)
    let fehlerdetails: any;
    if (data && typeof data === 'object' && !(data instanceof Blob)) {
        fehlerdetails = {
            ...data,
            status: data.status ?? status,
            message: (data as any).detail ?? (data as any).title ?? error?.message,
            stack: error?.stack,
            info: additionalInfo
        };
    } else {
        // Fallback: generische Fehlerdaten
        fehlerdetails = {
            status,
            message: error?.message,
            stack: error?.stack,
            info: additionalInfo
        };
    }

    const currentKunde = router.currentRoute.value.params.kunde as string | undefined;
    const eingeloggt = istBenutzerAngemeldet();
    const target = fehlerdetails.status === 403 ? RouterHelpers.toAccessDenied(eingeloggt, currentKunde) : RouterHelpers.toError(eingeloggt, currentKunde);

    return {
        ...target,
        state: {
            fehlerdetails
        } as any
    };
};

/**
 * Hilfsfunktion: Navigiert zur Fehlerseite mit Fehlerdetails
 */
export const navigateToErrorPage = (router: Router, error: any, additionalInfo?: string): void => {
    // Flag am Error-Objekt zum Unterdrücken der globalen Fehlerseite
    if (error?.[SKIP_GLOBAL_ERROR_HANDLING_FLAG]) {
        return;
    }

    router.push(getFehlerRoute(router, error, additionalInfo));
};

/**
 * Registriert globale Error-Handler für die Anwendung
 * - Vue Error Handler für Fehler in Komponenten
 * - Window Error Handler für JavaScript-Fehler
 * - Unhandled Rejection Handler für Promise-Fehler
 * - Router Error Handler für Fehler in Navigation Guards
 */
export const setupGlobalErrorHandlers = (app: App, router: Router): void => {
    // Globaler Vue Error Handler für unbehandelte Fehler in Komponenten
    app.config.errorHandler = (err: any, instance, info) => {
        navigateToErrorPage(router, err, info);
    };

    // Globaler Handler für unbehandelte JavaScript-Fehler außerhalb von Vue
    window.addEventListener('error', (event) => {
        navigateToErrorPage(router, event.error);
    });

    // Globaler Handler für unbehandelte Promise-Rejections
    window.addEventListener('unhandledrejection', (event) => {
        navigateToErrorPage(router, event.reason);
    });

    // Globaler Router Error Handler
    // Wichtig: Fehler in Navigation Guards (async beforeEach/beforeEnter)
    // lösen kein 'unhandledrejection' aus, da der Router sie abfängt.
    router.onError((err) => {
        navigateToErrorPage(router, err);
    });
};
